/**
 * Drag-handle gutter — renders a ⋮⋮ marker on the first line of every
 * MovableRange (define / for / content / meta / import / use). The marker
 * itself initiates a drag via the controller in `drag-controller.ts`.
 *
 * Sits to the left of the line-number gutter, between bookmarks and lines.
 */

import type { Extension } from "@codemirror/state";
import { GutterMarker, gutter } from "@codemirror/view";
import { t as translate } from "@magic-md/i18n";
import {
    type MovableRange,
    getMovableRanges,
} from "./movable-ranges";

function tr(key: string, params?: Record<string, unknown>): string {
    return translate("editor", key, params);
}

const DRAG_MARKER_CLASS = "cm-magic-drag-handle";
const DRAG_HANDLE_DATA_ATTR = "data-magic-drag-id";

class DragHandleMarker extends GutterMarker {
    constructor(public movable: MovableRange) {
        super();
    }

    eq(other: GutterMarker): boolean {
        return (
            other instanceof DragHandleMarker &&
            other.movable.id === this.movable.id
        );
    }

    toDOM(): HTMLElement {
        const el = document.createElement("span");
        el.className = DRAG_MARKER_CLASS;
        el.setAttribute(DRAG_HANDLE_DATA_ATTR, this.movable.id);
        el.title = tr("syntax.widget.drag.moveNamed", { label: this.movable.label });
        // ⋮⋮ glyph (two vertical dots) — purely visual; the controller listens
        // for pointerdown on `.cm-magic-drag-handle` to start drag.
        el.textContent = "⋮⋮";
        return el;
    }
}

class DragHandleSpacer extends GutterMarker {
    toDOM(): HTMLElement {
        const el = document.createElement("span");
        el.className = "cm-magic-drag-handle-spacer";
        return el;
    }
}

const dragHandleSpacer = new DragHandleSpacer();

/** Public so the controller can read drag id from a target element. */
export const DRAG_HANDLE_ATTR = DRAG_HANDLE_DATA_ATTR;
export const DRAG_HANDLE_CSS_CLASS = DRAG_MARKER_CLASS;

export function magicDragGutter(): Extension {
    return gutter({
        class: "cm-magic-drag-gutter",
        initialSpacer: () => dragHandleSpacer,
        lineMarker: (view, line) => {
            const ranges = getMovableRanges(view.state);
            if (ranges.length === 0) return dragHandleSpacer;
            const lineNo = view.state.doc.lineAt(line.from).number;
            // Show the handle only on a movable range's first line.
            const r = ranges.find((mr) => mr.fromLine === lineNo);
            return r ? new DragHandleMarker(r) : dragHandleSpacer;
        },
        lineMarkerChange: (update) => {
            // Re-evaluate markers when the doc structure may have changed.
            return update.docChanged;
        },
    });
}
